import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from 'src/environments/environment';
import { DebugService } from './debug.service';

interface MemoryInfo {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

interface PerformanceMemory extends Performance {
  memory?: MemoryInfo;
}

// Global flag to prevent repeated logging across service reloads during development
declare global {
  interface Window {
    __waytr_memory_api_warning_logged?: boolean;
  }
}

@Injectable({ providedIn: 'root' })
export class MemoryMonitorService {
  private readonly MEMORY_THRESHOLD_MB = 100;
  private readonly MEMORY_CRITICAL_THRESHOLD_MB = 150;
  private readonly CHECK_INTERVAL_MS = 30000; // 30 seconds

  readonly #platformId = inject(PLATFORM_ID);
  readonly #debug = inject(DebugService);
  private monitoringInterval?: ReturnType<typeof setInterval>;
  private cleanupCallbacks = new Set<() => void>();
  private serviceCleanupMap = new Map<string, () => void>();

  constructor() {
    if (isPlatformBrowser(this.#platformId)) {
      if (this.isMemoryAPIAvailable()) {
        this.startMonitoring();
      } else if (!window.__waytr_memory_api_warning_logged && !environment.production) {
        console.info('[Memory Monitor] Memory API not available in this browser/environment - memory monitoring disabled');
        window.__waytr_memory_api_warning_logged = true;
      }
    }
  }

  private isMemoryAPIAvailable(): boolean {
    return 'memory' in performance;
  }

  public isMonitoringActive(): boolean {
    return this.isMemoryAPIAvailable() && isPlatformBrowser(this.#platformId);
  }

  private getMemoryInfo(): MemoryInfo | null {
    if (!this.isMemoryAPIAvailable()) {
      return null;
    }

    const performanceMemory = performance as PerformanceMemory;
    return performanceMemory.memory || null;
  }

  public getCurrentMemoryUsage(): {
    usedMB: number;
    totalMB: number;
    limitMB: number;
    processMemoryMB?: number;
    domNodes?: number;
  } | null {
    const memory = this.getMemoryInfo();
    if (!memory) return null;

    const result = {
      usedMB: Math.round(memory.usedJSHeapSize / 1024 / 1024),
      totalMB: Math.round(memory.totalJSHeapSize / 1024 / 1024),
      limitMB: Math.round(memory.jsHeapSizeLimit / 1024 / 1024),
    };

    // Try to get additional memory information
    const additionalInfo = this.getAdditionalMemoryInfo();

    return {
      ...result,
      ...additionalInfo,
    };
  }

  private getAdditionalMemoryInfo(): { processMemoryMB?: number; domNodes?: number } {
    const info: { processMemoryMB?: number; domNodes?: number } = {};

    // Try to get process memory (experimental)
    if ('memory' in performance && (performance as PerformanceMemory).memory?.usedJSHeapSize) {
      // Estimate total process memory (this is a rough approximation)
      const jsHeapMB = (performance as PerformanceMemory).memory!.usedJSHeapSize / 1024 / 1024;
      // Typically total process memory is 3-5x JS heap size
      info.processMemoryMB = Math.round(jsHeapMB * 4);
    }

    // Count DOM nodes as a memory indicator
    if (typeof document !== 'undefined') {
      info.domNodes = document.querySelectorAll('*').length;
    }

    return info;
  }

  public registerCleanupCallback(callback: () => void): () => void {
    this.cleanupCallbacks.add(callback);

    // Return unregister function
    return () => {
      this.cleanupCallbacks.delete(callback);
    };
  }

  /**
   * Register a service's cleanup method for memory pressure scenarios.
   * Services with large data structures or expensive operations should register here.
   *
   * @param serviceName - Unique identifier for the service (e.g., 'RecipesStateService')
   * @param cleanupFn - Lightweight cleanup function that clears large data but keeps essential state
   * @returns Unregister function to remove the callback
   *
   * @example
   * ```typescript
   * constructor() {
   *   this.#memoryMonitorService.registerServiceCleanup('RecipesStateService', () => {
   *     this.lightweightCleanup();
   *   });
   * }
   * ```
   */
  public registerServiceCleanup(serviceName: string, cleanupFn: () => void): () => void {
    if (this.serviceCleanupMap.has(serviceName)) {
      if (!environment.production) {
        console.warn(`[Memory Monitor] Service '${serviceName}' cleanup already registered - replacing`);
      }
    }

    this.serviceCleanupMap.set(serviceName, cleanupFn);

    this.#debug.logMemoryEvent('registered', `Registered cleanup for service: ${serviceName}`);

    // Return unregister function
    return () => {
      this.serviceCleanupMap.delete(serviceName);
      this.#debug.logMemoryEvent('unregistered', `Unregistered cleanup for service: ${serviceName}`);
    };
  }

  /**
   * Trigger cleanup for all registered services.
   * This is called automatically on memory pressure, but can be called manually.
   *
   * @param servicesOnly - If true, only trigger service cleanups (not generic callbacks)
   * @public
   */
  public triggerServiceCleanup(servicesOnly: boolean = false): void {
    this.#debug.logMemoryEvent('cleanup', `Triggering service cleanup (${this.serviceCleanupMap.size} services registered)`);

    // Execute all registered service cleanups
    this.serviceCleanupMap.forEach((cleanupFn, serviceName) => {
      try {
        cleanupFn();
        this.#debug.logMemoryEvent('cleanup', `Cleaned up service: ${serviceName}`);
      } catch (error) {
        if (!environment.production) {
          console.error(`[Memory Monitor] Service cleanup failed for ${serviceName}:`, error);
        }
      }
    });

    // Also trigger generic cleanup callbacks unless servicesOnly is true
    if (!servicesOnly) {
      this.triggerCleanup();
    }
  }

  private startMonitoring(): void {
    this.monitoringInterval = setInterval(() => {
      this.checkMemoryUsage();
    }, this.CHECK_INTERVAL_MS);
  }

  private checkMemoryUsage(): void {
    const memoryInfo = this.getCurrentMemoryUsage();
    if (!memoryInfo) return;

    const usedMB = memoryInfo.usedMB;

    if (usedMB > this.MEMORY_CRITICAL_THRESHOLD_MB) {
      if (!environment.production) {
        console.error(`[Memory Monitor] CRITICAL: Memory usage is ${usedMB}MB (threshold: ${this.MEMORY_CRITICAL_THRESHOLD_MB}MB)`);
      }
      this.triggerEmergencyCleanup();
    } else if (usedMB > this.MEMORY_THRESHOLD_MB) {
      if (!environment.production) {
        console.warn(`[Memory Monitor] HIGH: Memory usage is ${usedMB}MB (threshold: ${this.MEMORY_THRESHOLD_MB}MB)`);
      }
      this.triggerCleanup();
    }
    // Removed healthy status logging to reduce console noise
  }

  private triggerCleanup(): void {
    this.#debug.logMemoryEvent('cleanup', 'Triggering cleanup callbacks...');
    this.cleanupCallbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        if (!environment.production) {
          console.error('[Memory Monitor] Cleanup callback failed:', error);
        }
      }
    });
  }

  private triggerEmergencyCleanup(): void {
    this.#debug.logMemoryEvent('emergency-cleanup', 'Triggering EMERGENCY cleanup...');

    // Run all cleanup callbacks
    this.triggerCleanup();

    // Force garbage collection if available
    if ('gc' in window && typeof (window as { gc?: () => void }).gc === 'function') {
      try {
        (window as { gc?: () => void }).gc!();
        this.#debug.logMemoryEvent('gc-triggered', 'Manual garbage collection triggered');
      } catch (error) {
        if (!environment.production) {
          console.warn('[Memory Monitor] Manual GC failed:', error);
        }
      }
    }

    // Clear any cached images
    this.clearImageCache();
  }

  private clearImageCache(): void {
    // Clear blob URLs that might be cached
    const images = document.getElementsByTagName('img');
    Array.from(images).forEach((img) => {
      if (img.src.startsWith('blob:')) {
        URL.revokeObjectURL(img.src);
      }
    });
  }

  public stopMonitoring(): void {
    if (this.monitoringInterval) {
      clearInterval(this.monitoringInterval);
      this.monitoringInterval = undefined;
    }
    this.cleanupCallbacks.clear();
  }

  public getMemoryStats(): string {
    const memoryInfo = this.getCurrentMemoryUsage();
    if (!memoryInfo) {
      // Only log the warning once to avoid spam - use global flag to persist across reloads
      if (!window.__waytr_memory_api_warning_logged && !environment.production) {
        console.warn('[Memory Monitor] Memory API not available - stats unavailable');
        window.__waytr_memory_api_warning_logged = true;
      }
      return 'Memory API not available';
    }

    const percentage = Math.round((memoryInfo.usedMB / memoryInfo.limitMB) * 100);

    let stats = `JS Heap: ${memoryInfo.usedMB}MB / ${memoryInfo.limitMB}MB (${percentage}%)`;

    if (memoryInfo.processMemoryMB) {
      stats += ` | Est. Process: ~${memoryInfo.processMemoryMB}MB`;
    }

    if (memoryInfo.domNodes) {
      stats += ` | DOM: ${memoryInfo.domNodes} nodes`;
    }

    return stats;
  }

  public getDetailedMemoryStats(): {
    jsHeap: { used: number; total: number; limit: number; percentage: number };
    estimated: { processMemory?: number; domNodes?: number };
    notes: string[];
  } | null {
    const memoryInfo = this.getCurrentMemoryUsage();
    if (!memoryInfo) return null;

    return {
      jsHeap: {
        used: memoryInfo.usedMB,
        total: memoryInfo.totalMB,
        limit: memoryInfo.limitMB,
        percentage: Math.round((memoryInfo.usedMB / memoryInfo.limitMB) * 100),
      },
      estimated: {
        processMemory: memoryInfo.processMemoryMB,
        domNodes: memoryInfo.domNodes,
      },
      notes: [
        'JS Heap = JavaScript memory only (what performance.memory reports)',
        'Process Memory = Estimated total (includes DOM, CSS, images, etc.)',
        'Chrome DevTools shows total process memory, not just JS heap',
        'For accurate total memory, use Chrome DevTools Memory tab',
      ],
    };
  }
}
